import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import GlassNav from "@/components/GlassNav";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, QrCode, Image as ImageIcon, Heart } from "lucide-react";

export default function Dashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [weddings, setWeddings] = useState([]);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    api.get("/weddings").then((r) => setWeddings(r.data)).finally(() => setBusy(false));
  }, [user, loading, nav]);

  const totals = weddings.reduce((acc, w) => ({
    memories: acc.memories + (w.memory_count || 0),
    wishes: acc.wishes + (w.wish_count || 0),
  }), { memories: 0, wishes: 0 });

  return (
    <div className="min-h-screen relative">
      <GlassNav />
      <div className="pt-28 pb-24 px-4 max-w-6xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-widest text-neutral-500">Your dashboard</div>
            <h1 className="font-serif text-4xl sm:text-5xl mt-1">Hello, {user?.name}</h1>
          </div>
          <Link to="/dashboard/new" data-testid="btn-new-wedding" className="btn-accent rounded-full px-5 py-3 inline-flex items-center gap-2 text-sm">
            <Plus className="w-4 h-4" /> New wedding
          </Link>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mb-10">
          <StatCard label="Weddings" value={weddings.length} icon={<QrCode className="w-4 h-4" />} testid="stat-weddings" />
          <StatCard label="Memories" value={totals.memories} icon={<ImageIcon className="w-4 h-4" />} testid="stat-memories" />
          <StatCard label="Wishes" value={totals.wishes} icon={<Heart className="w-4 h-4" />} testid="stat-wishes" />
        </div>

        <div className="mb-4 font-serif text-2xl">Your weddings</div>
        {busy ? (
          <div className="glass p-10 text-center text-neutral-500">Loading...</div>
        ) : weddings.length === 0 ? (
          <div className="glass p-10 text-center">
            <div className="font-serif text-2xl mb-2">Start your first wedding</div>
            <p className="text-neutral-600 mb-4">Design your page, print your QR, collect every memory.</p>
            <Link to="/dashboard/new" className="btn-accent rounded-full px-5 py-3 inline-flex items-center gap-2 text-sm" data-testid="empty-create">
              <Plus className="w-4 h-4" /> Create wedding
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {weddings.map((w) => (
              <Link key={w.id} to={`/dashboard/w/${w.id}`} data-testid={`wedding-card-${w.slug}`} className="glass overflow-hidden group">
                <div className="h-40 bg-neutral-200 relative">
                  {w.cover_image && <img src={w.cover_image} alt="" className="w-full h-full object-cover" />}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent" />
                  <div className="absolute bottom-3 left-4 right-4">
                    <div className="font-serif text-white text-2xl leading-tight drop-shadow">{w.display_name}</div>
                  </div>
                </div>
                <div className="p-4 flex items-center justify-between text-xs text-neutral-600">
                  <span>{w.date || "—"}</span>
                  <span>{w.memory_count || 0} memories · {w.wish_count || 0} wishes</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, icon, testid }) {
  return (
    <div className="glass p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-neutral-500 uppercase tracking-widest">
        {icon} {label}
      </div>
      <div className="font-serif text-4xl mt-2">{value}</div>
    </div>
  );
}
